import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Camera, X } from 'react-feather';
import menuApi from 'api/requests/menu';
import imageUploadApi from 'api/requests/imageUpload';
import orderStationsApi from 'api/requests/orderStations';
import colors from 'theme/colors';

export default function ProductForm({ slug, categories, initialData, onSaved, onCancel }) {
    const [name, setName] = useState(initialData?.name || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [basePrice, setBasePrice] = useState(initialData?.base_price || '');
    const [categoryId, setCategoryId] = useState(initialData?.menu_category_id || '');
    const [sortOrder, setSortOrder] = useState(initialData?.sort_order ?? '');
    const [orderStationId, setOrderStationId] = useState(initialData?.order_station_id ?? initialData?.order_station?.id ?? '');
    const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
    const [isPublic, setIsPublic] = useState(initialData?.is_public ?? true);
    const [stations, setStations] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState(
        initialData?.option_groups?.map(g => g.id) || []
    );
    const [preUploadedImage, setPreUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(initialData?.image?.thumb || null);
    const [imageError, setImageError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

    function handleImageChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_IMAGE_SIZE) {
            setImageError('Imagem muito grande. Tamanho máximo: 10MB.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        setImageError(null);
        setPreUploadedImage(file);
        setImagePreview(URL.createObjectURL(file));
    }

    function removeImage() {
        setPreUploadedImage(null);
        setImagePreview(null);
        setImageError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    async function uploadImage(file) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', 'products');
        try {
            const { data } = await imageUploadApi.uploadWithFolder(formData);
            return data;
        } catch (err) {
            if (err?.response?.status === 413) {
                throw new Error('image_too_large');
            }
            throw err;
        }
    }

    useEffect(() => {
        menuApi.getOptionGroups(slug)
            .then(({ data }) => setAllGroups(data.data || []))
            .catch(() => {});
        orderStationsApi.list(slug)
            .then(({ data }) => setStations(data.data || data || []))
            .catch(() => {});
    }, []);

    function toggleGroup(id) {
        setSelectedGroups(prev =>
            prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
        );
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!name.trim() || !categoryId || !basePrice) {
            setError('Nome, categoria e preço são obrigatórios');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            let imageId = null;
            if (preUploadedImage) {
                const imageData = await uploadImage(preUploadedImage);
                imageId = imageData?.id || null;
            }

            const payload = {
                name: name.trim(),
                description: description.trim() || null,
                base_price: parseFloat(basePrice),
                menu_category_id: Number(categoryId),
                ...(imageId && { image_id: imageId }),
                ...(sortOrder !== '' && { sort_order: Number(sortOrder) }),
                order_station_id: orderStationId ? Number(orderStationId) : null,
                is_active: isActive,
                is_public: isPublic,
            };
            let saved;
            if (initialData) {
                const { data } = await menuApi.updateProduct(slug, initialData.id, payload);
                saved = data;
            } else {
                const { data } = await menuApi.createProduct(slug, payload);
                saved = data;
            }
            // Sync option groups only when there are selections (backend rejects empty array)
            if (selectedGroups.length > 0) {
                const { data: synced } = await menuApi.syncOptionGroups(slug, saved.id, selectedGroups);
                onSaved(synced);
            } else {
                onSaved(saved);
            }
        } catch (err) {
            if (err?.message === 'image_too_large') {
                setImageError('Imagem muito grande. Tamanho máximo: 10MB.');
            } else {
                setError('Erro ao salvar. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Form onSubmit={handleSubmit}>
            <Field>
                <Label>Foto do produto</Label>
                <ImageUploadArea onClick={() => fileInputRef.current?.click()} $hasImage={!!imagePreview}>
                    {imagePreview ? (
                        <>
                            <ImagePreview src={imagePreview} alt='preview' />
                            <RemoveImageBtn
                                type='button'
                                onClick={e => { e.stopPropagation(); removeImage(); }}
                            >
                                <X size={14} />
                            </RemoveImageBtn>
                        </>
                    ) : (
                        <UploadPlaceholder>
                            <Camera size={22} color={colors.nightLight} />
                            <UploadHint>Clique para adicionar foto</UploadHint>
                        </UploadPlaceholder>
                    )}
                </ImageUploadArea>
                <HiddenInput
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    onChange={handleImageChange}
                />
                {imageError
                    ? <ImageErrorText>{imageError}</ImageErrorText>
                    : <ImageHint>JPG, PNG ou WebP · Máx. 10MB</ImageHint>
                }
            </Field>

            <Field>
                <Label>Nome *</Label>
                <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder='Ex: Smash Burger Clássico'
                    autoFocus
                />
            </Field>

            <Field>
                <Label>Descrição</Label>
                <TextArea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={2}
                    placeholder='Ingredientes, detalhes...'
                />
            </Field>

            <Field>
                <Label>Preço base *</Label>
                <PriceWrapper>
                    <CurrencyPrefix>R$</CurrencyPrefix>
                    <PriceInput
                        type='number'
                        step='0.01'
                        min='0'
                        value={basePrice}
                        onChange={e => setBasePrice(e.target.value)}
                        placeholder='0,00'
                    />
                </PriceWrapper>
            </Field>

            <Field>
                <Label>Categoria *</Label>
                <Select value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                    <option value=''>Selecionar</option>
                    {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </Select>
            </Field>

            <Field>
                <Label>Estação de pedido</Label>
                <Select value={orderStationId} onChange={e => setOrderStationId(e.target.value)}>
                    <option value=''>Nenhuma (automático)</option>
                    {stations.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                </Select>
            </Field>

            <Field style={{ maxWidth: 120 }}>
                <Label>Ordem</Label>
                <Input
                    type='number'
                    min='0'
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value)}
                    placeholder='Ex: 1'
                />
            </Field>

            {allGroups.length > 0 && (
                <Field>
                    <Label>Grupos de opções</Label>
                    <GroupList>
                        {allGroups.map(group => (
                            <GroupChip
                                key={group.id}
                                type='button'
                                $selected={selectedGroups.includes(group.id)}
                                onClick={() => toggleGroup(group.id)}
                            >
                                {group.name}
                            </GroupChip>
                        ))}
                    </GroupList>
                </Field>
            )}

            <VisibilitySection>
                <SectionLabel>Visibilidade</SectionLabel>
                <ToggleRow>
                    <ToggleInfo>
                        <ToggleName>Ativo</ToggleName>
                        <ToggleHint>Produto aparece no cardápio</ToggleHint>
                    </ToggleInfo>
                    <ToggleSwitch
                        $on={isActive}
                        onClick={() => setIsActive(v => !v)}
                        type="button"
                        role="switch"
                        aria-checked={isActive}
                    >
                        <ToggleKnob $on={isActive} />
                    </ToggleSwitch>
                </ToggleRow>
                <ToggleRow $disabled={!isActive}>
                    <ToggleInfo>
                        <ToggleName>Cardápio público</ToggleName>
                        <ToggleHint>Clientes podem ver este produto</ToggleHint>
                    </ToggleInfo>
                    <ToggleSwitch
                        $on={isPublic}
                        onClick={() => isActive && setIsPublic(v => !v)}
                        type="button"
                        role="switch"
                        aria-checked={isPublic}
                        disabled={!isActive}
                    >
                        <ToggleKnob $on={isPublic} />
                    </ToggleSwitch>
                </ToggleRow>
            </VisibilitySection>

            {error && <ErrorText>{error}</ErrorText>}

            <SubmitBtn type='submit' disabled={loading}>
                {loading ? 'Salvando...' : initialData ? 'Salvar alterações' : 'Criar produto'}
            </SubmitBtn>
        </Form>
    );
}

const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`;

const ImageUploadArea = styled.div`
    width: 100%; height: 140px; border-radius: 12px; overflow: hidden; position: relative;
    border: 1.5px dashed ${({ $hasImage }) => $hasImage ? 'transparent' : colors.neutralDark};
    background: ${({ $hasImage }) => $hasImage ? 'transparent' : colors.neutralBackground || '#f9f9f9'};
    cursor: pointer; transition: border-color 0.15s;
    &:hover { border-color: ${colors.occasionalPurple}; }
`;
const ImagePreview = styled.img`width: 100%; height: 100%; object-fit: cover;`;
const RemoveImageBtn = styled.button`
    position: absolute; top: 8px; right: 8px;
    width: 26px; height: 26px; border-radius: 50%;
    background: rgba(0,0,0,0.55); color: #fff; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
`;
const UploadPlaceholder = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 100%; gap: 8px;
`;
const UploadHint = styled.span`font-size: 13px; color: #999;`;
const ImageHint = styled.span`font-size: 12px; color: ${colors.nightLight};`;
const ImageErrorText = styled.span`font-size: 12px; color: ${colors.danger}; font-weight: 500;`;
const HiddenInput = styled.input`display: none;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const Label = styled.label`font-size: 13px; font-weight: 600; color: ${colors.nightDark};`;
const Input = styled.input`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; outline: none; color: ${colors.nightDark}; background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: #999; }
`;
const TextArea = styled.textarea`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; outline: none; color: ${colors.nightDark}; resize: vertical; background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
`;
const Row = styled.div`display: flex; gap: 12px;`;
const PriceWrapper = styled.div`
    display: flex; align-items: center;
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; background: #fff;
    overflow: hidden; transition: border-color 0.15s;
    &:focus-within { border-color: ${colors.occasionalPurple}; }
`;
const CurrencyPrefix = styled.span`
    padding: 12px 10px 12px 14px; font-size: 15px; color: #666;
    font-weight: 600; border-right: 1px solid ${colors.neutralDark}; background: ${colors.neutralDark};
`;
const PriceInput = styled.input`
    flex: 1; border: none; padding: 12px 14px; font-size: 15px; outline: none;
    color: ${colors.nightDark}; background: transparent;
`;
const Select = styled.select`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; background: #fff; outline: none; color: ${colors.nightDark};
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
`;
const GroupList = styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px;`;
const GroupChip = styled.button`
    padding: 7px 14px; border-radius: 20px; font-size: 13px; cursor: pointer; font-weight: 500;
    border: 1.5px solid ${({ $selected }) => $selected ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $selected }) => $selected ? `${colors.occasionalPurple}15` : 'transparent'};
    color: ${({ $selected }) => $selected ? colors.occasionalPurple : colors.night};
    transition: all 0.15s;
`;
const VisibilitySection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    overflow: hidden;
`;
const SectionLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.nightLight};
    padding: 10px 14px 6px;
    background: ${colors.neutralBackground || '#f9f9f9'};
    border-bottom: 1px solid ${colors.neutralDark};
`;
const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    gap: 12px;
    opacity: ${({ $disabled }) => $disabled ? 0.45 : 1};
    transition: opacity 0.15s;
    & + & { border-top: 1px solid ${colors.neutralDark}; }
`;
const ToggleInfo = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const ToggleName = styled.span`font-size: 14px; font-weight: 600; color: ${colors.nightDark};`;
const ToggleHint = styled.span`font-size: 12px; color: ${colors.nightLight};`;
const ToggleSwitch = styled.button`
    width: 42px;
    height: 24px;
    border-radius: 12px;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    background: ${({ $on }) => $on ? colors.occasionalGreen : colors.neutralDark};
    position: relative;
    flex-shrink: 0;
    transition: background 0.2s;
    padding: 0;
`;
const ToggleKnob = styled.div`
    position: absolute;
    top: 3px;
    left: ${({ $on }) => $on ? '21px' : '3px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: left 0.2s;
`;
const ErrorText = styled.p`color: ${colors.danger}; font-size: 13px; margin: 0;`;
const SubmitBtn = styled.button`
    padding: 14px; border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 15px; font-weight: 700; border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    &:hover:not(:disabled) { opacity: 0.9; }
`;
